import React, {useMemo, useState} from 'react';
import {
    MaterialReactTable,
    MRT_BottomToolbar,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner,
    useMaterialReactTable,
} from 'material-react-table';
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import ModalReplacementWorker from "./ModalReplacementWorker";
import WorkerUtil from "../../../../../../utils/orders/WorkerUtil";
import ModalExecuteJob from "./ModalExecuteJob";
import DateTimeUtil from "../../../../../../utils/common/DateTimeUtil";
import DateRangeFilter from "../../../../filter/DateRangeFilter";
import MenuItem from "@mui/material/MenuItem";
import {Tooltip, useMediaQuery} from "@mui/material";
import Box from "@mui/material/Box";
import ModalConfirm from "../../../../modal/ModalConfirm";
import useTechnicalMapRowActions from "./useTechnicalMapRowActions";
import TechnicalMapJobCardList from "./TechnicalMapJobCardList";
import {TECHNICAL_MAP_TABLET_MEDIA_QUERY} from "./technicalMapBreakpoints";
import {formatJobCount, formatJobNormOfTime} from "./technicalMapFormatters";
import {getTechnicalMapFilterPlaceholder} from "./technicalMapTabletFilterUtils";

const TechnicalMapTable = ({
                               data, setData, estimateIsCurrent, tableOptions,
                               tableOptionsHandle, handleModalAlert, production
                           }) => {
    const isTablet = useMediaQuery(TECHNICAL_MAP_TABLET_MEDIA_QUERY, {noSsr: true});
    const [showColumnFilters, setShowColumnFilters] = useState(false);
    const [grouping, setGrouping] = useState(['typeJobName']);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const {
        isSettingsRole,
        menuItems,
        isActionActive,
        openModalSimilarWorker,
        closeSimilarWorker,
        currentIdRow,
        openModalExecuteJob,
        closeExecuteJob,
        currentDate,
        setCurrentDate,
        onExecute,
        modalCancelExecuteJob,
        handleCloseCancelExecuteJob,
        handleSubmitCancelExecuteJob,
        isDelete,
        executeJob,
        cancelExecuteJob,
        deleteJob,
    } = useTechnicalMapRowActions({setData, estimateIsCurrent, handleModalAlert});
    const jobs = Array.isArray(data) ? data : [];

    const columns = useMemo(
        () => [
            {
                header: 'Тип работы',
                accessorKey: 'typeJobName',
                enableEditing: false,
                enableGrouping: true,
                minSize: 35,
                maxSize: 50,
                muiFilterTextFieldProps: {
                    placeholder: getTechnicalMapFilterPlaceholder('Тип работы'),
                },
            },
            {
                header: 'Материал',
                accessorKey: 'materialName',
                enableColumnActions: false,
                enableEditing: false,
                minSize: 50,
                muiFilterTextFieldProps: {
                    placeholder: getTechnicalMapFilterPlaceholder('Материал'),
                },
            },
            {
                header: 'Количество',
                accessorKey: 'count',
                enableColumnActions: false,
                enableEditing: false,
                minSize: 10,
                Cell: ({cell}) => formatJobCount(cell.getValue()),
                muiFilterTextFieldProps: {
                    placeholder: getTechnicalMapFilterPlaceholder('Количество'),
                },
                filterFn: (row, id, filterValue) => {
                    if (!filterValue) {
                        return true;
                    }
                    const formatted = formatJobCount(row.getValue(id));
                    return formatted.toLowerCase().includes(String(filterValue).toLowerCase());
                },
            },
            {
                id: 'unitName',
                header: 'Ед.изм.',
                accessorKey: 'unitName',
                enableColumnActions: false,
                enableEditing: false,
                size: 8,
                muiFilterTextFieldProps: {
                    placeholder: getTechnicalMapFilterPlaceholder('Ед.изм.'),
                },
            },
            {
                header: 'НВ/ч',
                accessorKey: 'normOfTime',
                enableColumnActions: false,
                enableEditing: false,
                size: 8,
                Cell: ({cell}) => formatJobNormOfTime(cell.getValue()),
                muiFilterTextFieldProps: {
                    placeholder: getTechnicalMapFilterPlaceholder('НВ/ч'),
                },
                filterFn: (row, id, filterValue) => {
                    if (!filterValue) {
                        return true;
                    }
                    const formatted = formatJobNormOfTime(row.getValue(id));
                    return formatted.toLowerCase().includes(String(filterValue).toLowerCase());
                },
            },
            {
                header: 'Отметки',
                accessorKey: 'jobWorkers',
                id: 'jobWorkers',
                enableColumnActions: false,
                enableEditing: false,
                size: 8,
                Cell: ({cell}) => (
                    <div>
                        {WorkerUtil.convertWorkers(cell.getValue())}
                    </div>
                ),
                filterFn: (row, id, filterValue) => {
                    if (!filterValue) {
                        return true;
                    }
                    const haystack = WorkerUtil.convertWorkers(row.getValue(id)).toLowerCase();
                    return haystack.includes(String(filterValue).toLowerCase());
                },
                muiFilterTextFieldProps: {
                    placeholder: getTechnicalMapFilterPlaceholder('Отметки'),
                },
            },
            {
                accessorFn: (row) => {
                    if (row.completion === null) {
                    } else {
                        return new Date(row.completion)
                    }
                },
                header: 'Дата выполнения',
                id: 'completion',
                maxSize: 8,
                Cell: ({cell}) => {
                    if (cell.getValue() !== undefined) {
                        return cell.getValue().toLocaleDateString();
                    }
                },
                filterFn: (row, id, filterValue) => {
                    const startDate = filterValue?.startDate;
                    const endDate = filterValue?.endDate;
                    if (startDate && endDate) {
                        return (
                            DateTimeUtil.checkDateBefore(row.getValue(id), startDate) &&
                            DateTimeUtil.checkDateAfter(row.getValue(id), endDate)
                        );
                    }
                    return true;
                },
                Filter: ({column}) => <DateRangeFilter
                    column={column}
                />,
            },
            {
                header: 'Описание',
                accessorKey: 'metricName',
                id: 'metricName',
                enableColumnActions: false,
                enableEditing: false,
                size: 8,
                muiFilterTextFieldProps: {
                    placeholder: getTechnicalMapFilterPlaceholder('Описание'),
                },
            },
        ],
        [],
    );

    const table = useMaterialReactTable({
        columns,
        data: jobs,
        renderTopToolbar: ({table: toolbarTable}) => (
            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                padding: "7px",
                backgroundColor: (theme) => toolbarTable.getSelectedRowModel().rows.length > 0
                    ? theme.palette.primary.light
                    : "transparent",
            }}>
                <MRT_ToolbarAlertBanner table={toolbarTable}/>
                <Box sx={{display: "flex"}}>
                    <MRT_ToggleFiltersButton table={toolbarTable}/>
                    <MRT_GlobalFilterTextField table={toolbarTable}/>
                </Box>
            </Box>
        ),
        selectAllMode: "all",
        muiTableBodyCellProps: {
            sx: {
                p: '0px 0px 0px 10px',
                m: '0px 0px 0px 0px',
                border: "none",
                "&:after": {
                    display: "none",
                },
                "&:not(:last-of-type)": {
                    borderRight: '1px solid #EAEBEA',
                },
                "& a": {
                    color: "#006CCC",
                    textDecoration: "none",
                    "&:hover": {
                        textDecoration: "underline"
                    }
                },
            },
        },
        muiTableHeadProps: {
            sx: isTablet ? {display: 'none'} : undefined,
        },
        muiTableHeadCellProps: {
            sx: {
                border: '1px solid #EAEBEA',
                backgroundColor: "#F5F6FA",
                padding: "10px",
                paddingBottom: " 0.2rem",
                paddingTop: "0.2rem"
            },
        },
        muiTableBodyProps: {
            sx: isTablet ? {display: 'none'} : undefined,
        },
        muiTableBodyRowProps: ({row}) => ({
            sx: {
                height: '36px',
                backgroundColor: "white",
                "&:hover& td:after": {
                    display: "none",
                },
                "&.MuiTableRow-root.Mui-selected": {
                    backgroundColor: (theme) => theme.palette.primary.light70,
                    "&:hover": {
                        backgroundColor: (theme) => theme.palette.primary.light50,
                    },
                },
                "&:hover": {
                    "& td:after": {
                        display: "none",
                    },
                    backgroundColor: (theme) => theme.palette.primary.stroke,
                },
                '&:nth-of-type(even)': {
                    "&.MuiTableRow-root.Mui-selected": {
                        backgroundColor: (theme) => theme.palette.primary.light,
                        "&:hover": {
                            backgroundColor: (theme) => theme.palette.primary.light50,
                        },
                    },
                    backgroundColor: (theme) => theme.palette.primary.lightGrey,
                    "&:hover": {
                        backgroundColor: (theme) => theme.palette.primary.stroke,
                    },
                },
            },
        }),
        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: "",
                size: 10,
                grow: false,
            },
        },
        muiSearchTextFieldProps: {
            placeholder: `Поиск`,
            sx: {
                paddingTop: "5px",
                paddingBottom: "0px",
                "& .MuiCollapse-root": {
                    borderRadius: '12px',
                    height: 40,
                    width: 290,
                    "& .MuiSvgIcon-root": {
                        color: "#737478"
                    },
                    '& .MuiOutlinedInput-input': {
                        padding: 0
                    }
                }
            },
            variant: 'outlined',
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: isTablet
                    ? undefined
                    : (production ? `calc(100vh - 400px)` : `calc(100vh - 600px)`),
                display: isTablet ? 'none' : undefined,
            },
        },
        muiToolbarAlertBannerProps: {
            sx: {
                backgroundColor: (theme) => theme.palette.primary.light,
            },
        },
        onPaginationChange: (updater) => {
            tableOptionsHandle(updater(tableOptions));
        },
        onShowColumnFiltersChange: setShowColumnFilters,
        onGroupingChange: setGrouping,
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        paginateExpandedRows: false,
        state: {
            showGlobalFilter: true,
            showColumnFilters: isTablet ? false : showColumnFilters,
            pagination: tableOptions,
            grouping,
            globalFilter,
            columnFilters,
            sorting,
        },
        rowHeight: 13,
        columnSpacing: 1,
        enableGrouping: true,
        enableColumnFilters: true,
        initialState: {
            expanded: true,
            columnVisibility: {
                id: false,
                customerId: false,
                outputPriority: false
            },
            isFullScreen: false,
            density: "compact",
            pagination: {pageIndex: 0, pageSize: 50},
        },
        renderBottomToolbar: isTablet ? undefined : ({table: bottomTable}) => {
            const tableProps = bottomTable.getState().grouping.length > 0
                ? {
                    ...bottomTable,
                    options: {
                        ...bottomTable.options,
                        localization: {
                            ...bottomTable.options.localization,
                            rowsPerPage: `Всего строк: ${jobs.length}. Сгруппированных элементов на странице:`
                        }
                    }
                }
                : bottomTable;
            return <MRT_BottomToolbar table={tableProps}/>;
        },
        enableBottomToolbar: !isTablet,
        enableTopToolbar: !isTablet,
        getRowId: (originalRow) => originalRow.id,
        renderRowActionMenuItems: ({closeMenu, row}) => menuItems.map(el => {
            const active = isActionActive(el.type, row.original);
            return (
                <Tooltip arrow placement="left" title={!active ? "заблокировано" : ""} key={el.type}>
                    <MenuItem
                        onClick={() => {
                            active && el.handler(row.original);
                            closeMenu();
                        }}
                        sx={{
                            margin: "0 8px",
                            borderRadius: "4px",
                            opacity: !active && 0.5,
                            "&:hover": {backgroundColor: (theme) => theme.palette.primary.light}
                        }}
                    >
                        {el.text}
                    </MenuItem>
                </Tooltip>
            );
        }),
        localization: MRT_Localization_RU,
        enableRowActions: !isTablet,
        enableToolbarInternalActions: false,
    });

    const isGrouped = grouping.length > 0;

    const cardViewData = useMemo(() => {
        const rows = table.getPrePaginationRowModel().rows;

        if (isGrouped) {
            const groups = rows
                .filter((row) => row.getIsGrouped())
                .map((row) => {
                    const typeJobName = row.groupingValue ?? row.getValue('typeJobName');
                    return {
                        typeJobName: typeJobName != null && typeJobName !== ''
                            ? String(typeJobName)
                            : 'Без типа',
                        jobs: row.subRows.map((subRow) => subRow.original),
                    };
                });
            return {
                groups,
                jobs: groups.flatMap((group) => group.jobs),
            };
        }

        return {
            groups: null,
            jobs: rows
                .filter((row) => !row.getIsGrouped())
                .map((row) => row.original),
        };
    }, [table, jobs, globalFilter, columnFilters, grouping, sorting, isGrouped]);

    return (
        <div>
            {!isTablet && <MaterialReactTable table={table}/>}
            {isTablet && (
                <TechnicalMapJobCardList
                    data={cardViewData.jobs}
                    groups={cardViewData.groups}
                    isGrouped={isGrouped}
                    onGroupingChange={(checked) => setGrouping(checked ? ['typeJobName'] : [])}
                    globalFilter={globalFilter}
                    onGlobalFilterChange={setGlobalFilter}
                    columnFilters={columnFilters}
                    onColumnFiltersChange={setColumnFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    sortingKey={sorting.map(({id, desc}) => `${id}:${desc}`).join('|')}
                    setData={setData}
                    handleModalAlert={handleModalAlert}
                    tableOptions={tableOptions}
                    tableOptionsHandle={tableOptionsHandle}
                    isSettingsRole={isSettingsRole}
                    isActionActive={isActionActive}
                    onExecuteJob={executeJob}
                    onCancelExecuteJob={cancelExecuteJob}
                    onDeleteJob={deleteJob}
                />
            )}
            {openModalSimilarWorker && <ModalReplacementWorker
                open={openModalSimilarWorker}
                onClose={closeSimilarWorker}
                selectId={currentIdRow}
                setData={setData}
                handleModalAlert={handleModalAlert}
            />}
            {openModalExecuteJob && <ModalExecuteJob
                open={openModalExecuteJob}
                onClose={closeExecuteJob}
                date={currentDate}
                setDate={setCurrentDate}
                onExecute={onExecute}
            />}
            <ModalConfirm
                title={isDelete ? "Подтвердить удаление" : "Подтвердить отменить выполнение?"}
                modalOpen={modalCancelExecuteJob}
                handleClose={handleCloseCancelExecuteJob}
                handleSubmit={handleSubmitCancelExecuteJob}
                action={"Подтвердить"}
            />
        </div>
    );
};

export default TechnicalMapTable;
